import { fabric } from 'fabric';
import { type Rect } from '../types';
import { combinedPosition, overlappingObjects } from './geometry';

/**
 * Set of utitilies for deteminiming the "optimal" position to place duplicated
 * objects. Hook is calculateDuplicationOffset
 *
 * "Optimal" is fairly simplistic. We look for an open space in the following
 * order: right, left, top, bottom. If no open space is found, we offset the objects
 * by a factor of their width/height, essentially shifting them down and to the right
 */

// Constants for placement behavior
export const SPACING = 20; // Gap between selected and duplicated objects
export const DEFAULT_OFFSET = 0.5; // If no open spaces available, just shift right/down by 50%

export type PlacementDirection = 'left' | 'right' | 'top' | 'bottom';

export interface PlacementResult {
  left: number;
  top: number;
  isValid: boolean;
  withinBounds?: boolean;
}

// Just a helper method around combinedPosition, which works on Rects
function getSelectionBoundingRect(objects: fabric.Object[]): Rect {
  const rects = objects.map(obj => obj.getBoundingRect(true));
  return combinedPosition(rects);
}

// Gives us a bounding rect for where the duplicated object(s) would go, for collision
// testing purposes
function calculateProposedObject(
  selectedRect: Rect,
  direction: PlacementDirection,
  spacing: number
): fabric.Object {
  let left: number, top: number;
  switch (direction) {
    case 'left':
      left = selectedRect.left - selectedRect.width - spacing;
      top = selectedRect.top;
      break;
    case 'right':
      left = selectedRect.left + selectedRect.width + spacing;
      top = selectedRect.top;
      break;
    case 'top':
      left = selectedRect.left;
      top = selectedRect.top - selectedRect.height - spacing;
      break;
    case 'bottom':
      left = selectedRect.left;
      top = selectedRect.top + selectedRect.height + spacing;
      break;
  }

  return new fabric.Rect({
    left,
    top,
    width: selectedRect.width,
    height: selectedRect.height,
  });
}

function calculateDirectionalPlacement(
  selectedRect: Rect,
  direction: PlacementDirection,
  otherObjects: fabric.Object[],
  spacing: number,
  containerRect?: Rect
): PlacementResult {
  const obj = calculateProposedObject(selectedRect, direction, spacing);

  const hasNoCollisions =
    overlappingObjects(otherObjects, obj, true).length === 0;

  const isWithinBounds =
    hasNoCollisions && containerRect
      ? obj.isContainedWithinRect(
          { x: containerRect.left, y: containerRect.top },
          {
            x: containerRect.left + containerRect.width,
            y: containerRect.top + containerRect.height,
          },
          true
        )
      : true;

  return {
    left: obj.left,
    top: obj.top,
    isValid: hasNoCollisions,
    withinBounds: isWithinBounds,
  };
}

function calculateDefaultPlacement(selectedRect: Rect): PlacementResult {
  return {
    left: selectedRect.left + selectedRect.width * DEFAULT_OFFSET,
    top: selectedRect.top + selectedRect.height * DEFAULT_OFFSET,
    isValid: true,
  };
}

// Determines, with priority, which direction to place duplicated objects: right,
// left, top, bottom. If no open space is found, defaults to simple offset
function findOptimalPlacement(
  selectedRect: Rect,
  otherObjects: fabric.Object[],
  containerRect?: Rect,
  spacing = SPACING
): PlacementResult {
  const placementDirections: PlacementDirection[] = [
    'right',
    'left',
    'top',
    'bottom',
  ];

  const directions = placementDirections.map(direction =>
    calculateDirectionalPlacement(
      selectedRect,
      direction,
      otherObjects,
      spacing,
      containerRect
    )
  );

  // Priority: valid + within bounds > valid but outside bounds > default
  return (
    directions.find(dir => dir.isValid && dir.withinBounds) ??
    directions.find(dir => dir.isValid) ??
    calculateDefaultPlacement(selectedRect)
  );
}

// Finds optimal location for duplicated object(s) based on available space around
// selected object(s).
export function calculateDuplicationOffset(
  selectedObjects: fabric.Object[],
  otherObjects: fabric.Object[],
  containerRect?: Rect,
  spacing = SPACING
): { offsetX: number; offsetY: number } {
  const selectedRect = getSelectionBoundingRect(selectedObjects);
  const optimalPlacement = findOptimalPlacement(
    selectedRect,
    otherObjects,
    containerRect,
    spacing
  );

  return {
    offsetX: optimalPlacement.left - selectedRect.left,
    offsetY: optimalPlacement.top - selectedRect.top,
  };
}
